import type { CompanyExclusion, ExclusionReason } from '../types'
import { HEALTH_CHECKS } from '../config/health-checks.config'

/**
 * Monta a linha de procedência do recorte a partir da partição do backend.
 *
 * ## Por que isto existe
 *
 * A linha antiga somava três contadores calculados sobre bases diferentes
 * (`companiesExcluded`, `autoExcludeKeys.length` e `companiesProcessed −
 * companiesCount`). Como os conjuntos se sobrepõem, uma única empresa fora do
 * cálculo era anunciada três vezes — e o terceiro rótulo, "sem pedidos no
 * periodo", era simplesmente falso: vinha de uma subtração, não de contar
 * empresas sem pedido.
 *
 * `exclusions` é disjunta por construção, então agrupar e somar aqui é seguro.
 *
 * ## Fallback
 *
 * Resultado servido de cache gravado antes desta mudança não tem `exclusions`.
 * Nesse caso devolvemos lista vazia em vez de tentar reconstruir a partição a
 * partir dos contadores antigos: reconstruir traria de volta exatamente a
 * sobreposição que este módulo existe para eliminar. Melhor não afirmar nada do
 * que afirmar errado.
 */

/** Espelha EXCLUSION_LABELS do backend (company-disposition.ts). */
const REASON_LABELS: Record<ExclusionReason, string> = {
  low_invoice: 'com invoice abaixo do minimo',
  failed: 'falharam ao processar',
  no_orders: 'sem pedidos no periodo',
  no_cost: 'sem custo no periodo',
  health_check: 'excluidas por health check',
}

/** Vermelho para o que exige ação, âmbar para corte esperado. */
const REASON_COLORS: Record<ExclusionReason, string> = {
  low_invoice: '#f59e0b',
  failed: '#dc2626',
  no_orders: '#dc2626',
  no_cost: '#f59e0b',
  health_check: '#d97706',
}

/** Ordem de exibição: o que pede ação primeiro. */
const REASON_ORDER: ExclusionReason[] = [
  'failed',
  'no_orders',
  'health_check',
  'no_cost',
  'low_invoice',
]

export interface ExclusionGroup {
  reason: ExclusionReason
  count: number
  label: string
  color: string
  /** Detalhe legível: quais checks dispararam, ou que erro ocorreu. */
  detail?: string
}

export function buildExclusionSummary(exclusions: CompanyExclusion[] | undefined): ExclusionGroup[] {
  if (!exclusions || exclusions.length === 0) return []

  const byReason = new Map<ExclusionReason, CompanyExclusion[]>()
  for (const e of exclusions) {
    const list = byReason.get(e.reason)
    if (list) list.push(e)
    else byReason.set(e.reason, [e])
  }

  const groups: ExclusionGroup[] = []
  for (const reason of REASON_ORDER) {
    const items = byReason.get(reason)
    if (!items || items.length === 0) continue
    groups.push({
      reason,
      count: items.length,
      label: REASON_LABELS[reason],
      color: REASON_COLORS[reason],
      detail: buildDetail(reason, items),
    })
  }
  return groups
}

function buildDetail(reason: ExclusionReason, items: CompanyExclusion[]): string | undefined {
  if (reason === 'health_check') {
    // Qual check disparou é o dado acionável: `zero_influenced` significa ponte
    // site→GA4 quebrada naquela empresa, não "dado ruim" genérico. Uma empresa
    // pode disparar mais de um check, então a soma pode passar da contagem.
    const counts = new Map<string, number>()
    for (const item of items) {
      for (const checkId of item.checks ?? []) {
        counts.set(checkId, (counts.get(checkId) ?? 0) + 1)
      }
    }
    if (counts.size === 0) return undefined
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([checkId, n]) => `${n} ${HEALTH_CHECKS.find(c => c.id === checkId)?.label ?? checkId}`)
      .join(', ')
  }

  if (reason === 'failed') {
    // 413 = recorte largo demais (acionável: estreitar o período). Qualquer
    // outra coisa é erro de verdade. Separar os dois é o ponto: antes ambos
    // viravam o mesmo "falharam".
    const tooLarge = items.filter(i => /413|ORDERS_RESULT_TRUNCATED|too large/i.test(i.error ?? '')).length
    if (tooLarge === items.length) return 'periodo largo demais — estreite o intervalo'
    if (tooLarge > 0) return `${tooLarge} por periodo largo demais`
    return undefined
  }

  return undefined
}
